import { useCallback, useEffect, useMemo, useState } from "react";
import { Input, Menu, MenuProps, Select, message } from "antd";
import Sider from "antd/es/layout/Sider";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import styles from "./styles.module.css";
import { SidebarProps } from "./types";
import { Logo } from "./ui";
import useMediaQuery from "shared/hooks/useMediaQuery";
import { useTranslation } from "react-i18next";
import { useLazyGetServiceListQuery } from "features/service/api/serviceListApi";
import {
  useLazyGetHandbooksListQuery,
  useSyncDictionaryCodesMutation,
} from "features/handbookList/api/handbooksListApi";
import useDebounce from "shared/hooks/useDebounce";

const RESERVED_SLUGS = new Set<string>(["all-services", "all-handbooks"]);
const ALL_VALUE = "all";

export const Sidebar = ({
  collapsed,
  setSidebarCollapsed,
  roles,
}: SidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { t } = useTranslation();

  const [items, setItems] = useState<MenuProps["items"]>([]);
  const [search, setSearch] = useState("");
  const [selectedCode, setSelectedCode] = useState<string | undefined>(
    undefined
  );
  const debouncedSearch = useDebounce(search, 400);

  const baseHref = selectedCode === ALL_VALUE ? "/all" : `/${selectedCode}`;

  const screensLg = useMediaQuery("(min-width: 992px)");
  const handleMenuClick: MenuProps["onClick"] = () => {
    if (!screensLg) setSidebarCollapsed?.(true);
  };

  const isAllServicesActive =
    location.pathname === "/all-services" ||
    location.pathname.startsWith("/all-services/");
  const isAllHandbooksActive =
    location.pathname === "/all-handbooks" ||
    location.pathname.startsWith("/all-handbooks/");

  const [fetchServices, { data: servicesData, isFetching: servicesLoading }] =
    useLazyGetServiceListQuery();

  const [fetchHandbooksList, { data: handbooksData }] =
    useLazyGetHandbooksListQuery();

  const [syncCodes, { isLoading: isSyncing }] =
    useSyncDictionaryCodesMutation();

  const handleSync = useCallback(async () => {
    try {
      await syncCodes().unwrap();
      message.success(t("handbook.table.syncSuccess", "Коды синхронизированы"));
    } catch (e) {
      message.error(
        t("handbook.table.syncError", "Не удалось синхронизировать")
      );
      console.error(e);
    }
  }, [syncCodes, t]);

  useEffect(() => {
    fetchServices({ page: 1, size: 200 });
  }, [fetchServices]);

  useEffect(() => {
    const segs = location.pathname.split("/").filter(Boolean);
    const first = segs[0] || undefined;
    setSelectedCode(first && !RESERVED_SLUGS.has(first) ? first : undefined);
  }, [location.pathname]);

  const handleServiceChange = (value: string | undefined) => {
    setSelectedCode(value);

    if (!value) {
      setItems([]);
      navigate("/all-services");
    } else {
      // В том числе для «Все»: /all — тоже состояние «справочник не выбран».
      navigate(`/${encodeURIComponent(value)}`);
    }

    if (!screensLg) setSidebarCollapsed?.(true);
  };

  const handleChangeSearch = useCallback((val: string) => {
    setSearch(val);
  }, []);

  useEffect(() => {
    if (!selectedCode) {
      setItems([]);
      return;
    }

    const serviceCodes =
      selectedCode && selectedCode !== ALL_VALUE ? [selectedCode] : undefined;

    fetchHandbooksList({
      page: 1,
      size: 200,
      deleted: false,
      name: debouncedSearch || "",
      serviceCodes,
    });
  }, [selectedCode, fetchHandbooksList, debouncedSearch]);

  useEffect(() => {
    const content = handbooksData?.res?.content ?? [];
    const localized: MenuProps["items"] = content
      .filter((x) => typeof x.code === "string" && x.code.trim() !== "")
      .map((x) => {
        const code = String(x.code);
        const title = i18n.language?.startsWith("ky") ? x.nameKy : x.nameRu;
        return {
          key: code,
          label: <Link to={`${baseHref}/${code}`}>{title}</Link>,
        };
      });
    setItems(localized);
  }, [handbooksData, i18n.language, selectedCode]);

  const selectedKeys = useMemo(() => {
    const segs = location.pathname.split("/").filter(Boolean);
    return segs[1] ? [segs[1]] : [];
  }, [location.pathname]);

  const serviceOptions =
    (servicesData?.res?.content ?? [])
      .filter((x) => {
        const code = typeof x.code === "string" ? x.code.trim() : "";
        return code && !RESERVED_SLUGS.has(code);
      })
      .map((x) => ({
        value: String(x.code),
        label: i18n.language?.startsWith("ky") ? x.nameKy : x.nameRu,
      })) ?? [];

  const serviceOptionsWithAll = [
    { value: ALL_VALUE, label: "Все" },
    ...serviceOptions,
  ];

  return (
    <Sider
      className={`${styles.sidebar} adminsidebar`}
      trigger={null}
      collapsible
      width={250}
      collapsed={collapsed}
      onCollapse={setSidebarCollapsed}
      breakpoint="lg"
      collapsedWidth={0}
      onBreakpoint={(broken) => broken && setSidebarCollapsed?.(true)}
    >
      <div>
        <Link
          to="/"
          className={styles.logoWrap}
          onClick={() => !screensLg && setSidebarCollapsed?.(true)}
        >
          <Logo>
            <div className={styles.collapsedLogo}>
              <img
                style={{ width: 54 }}
                src={"/assets/logo/logocircle.svg"}
                alt=""
              />
              {!collapsed && <p>Общие справочники</p>}
            </div>
          </Logo>
        </Link>

        <div className={styles.selectWrap}>
          <Select
            options={serviceOptionsWithAll}
            onChange={handleServiceChange}
            onClear={() => handleServiceChange(undefined)}
            value={selectedCode}
            allowClear
            placeholder="Выберите сервис"
            className={styles.select}
            loading={servicesLoading}
          />

          {selectedCode && (
            <div>
              <Input
                className={styles.searchInput}
                placeholder={t("handbook.table.search", "Поиск")}
                value={search}
                allowClear
                onChange={(e) => handleChangeSearch(e.target.value)}
              />
            </div>
          )}
        </div>

        <div className={styles.scrollArea}>
          {selectedCode && (
            <Menu
              theme="light"
              mode="inline"
              selectedKeys={selectedKeys}
              items={items}
              className={styles.menuList}
              onClick={handleMenuClick}
            />
          )}
        </div>
      </div>

      <div className={styles.footerWrap}>
        <hr className={styles.footerDivider} />
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.syncButton}
            onClick={handleSync}
            disabled={isSyncing}
          >
            {isSyncing
              ? t("handbook.table.syncing", "Синхронизация...")
              : t("handbook.table.sync", "Синхронизировать")}
          </button>

          <NavLink
            to="/all-services"
            className={`${styles.footerLink} ${
              isAllServicesActive ? styles.activeLink : ""
            }`}
            onClick={() => !screensLg && setSidebarCollapsed?.(true)}
          >
            {t("handbook.table.allServices")}
          </NavLink>

          <NavLink
            to="/all-handbooks"
            className={`${styles.footerLink} ${
              isAllHandbooksActive ? styles.activeLink : ""
            }`}
            onClick={() => !screensLg && setSidebarCollapsed?.(true)}
          >
            {t("handbook.table.allHandbooks")}
          </NavLink>
        </div>
      </div>
    </Sider>
  );
};
